<template>
  <div class="count-down" :class="cssType + ' ' + locale" v-if="realTime > 0">
    <div class="count-down-title">{{ text }}<span v-if="cssType === 'pc'">:</span></div>
    <div class="count-down-time">
      <span class="box" v-if="countDown.day > 0">
        {{ countDown.day }} {{ $t('sf.day') }}
      </span>
      <span class="box">
        {{ countDown.hour }}
      </span>
      <span>:</span>
      <span class="box">
        {{ countDown.minute }}
      </span>
      <span>:</span>
      <span class="box">
        {{ countDown.second }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, watch } from 'vue'
import { isMobile } from '@/utils'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const cssType = isMobile ? 'mobile' : 'pc'
const emit = defineEmits(['end'])

const props = defineProps({
  realTime: {
    type: Number,
    default: 0,
  },
  activeStartTime: {
    type: [Number, String],
    default: 0,
  },
  activeEndTime: {
    type: [Number, String],
    default: 0,
  },
  pageCloseTime: {
    type: [Number, String],
    default: 0,
  },
})

const toTimestamp = (value) => {
  if (typeof value === 'number') return value < 100000000000 ? value * 1000 : value
  if (!value) return 0
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const startTime = computed(() => toTimestamp(props.activeStartTime))
const endTime = computed(() => toTimestamp(props.activeEndTime))
const closeTime = computed(() => toTimestamp(props.pageCloseTime))

const type = computed(() => {
  const now = Number(props.realTime) || 0
  const start = startTime.value
  const end = endTime.value || closeTime.value
  const close = closeTime.value || end

  if (!now || !start || !end || !close || now < start) return 'beforeActive'
  if (now < end) return 'activing'
  if (now < close) return 'rewarding'
  return 'end'
})

watch(type, (newValue, oldValue) => {
  if (newValue === 'end' && oldValue !== 'end') emit('end')
})

const text = computed(() => {
  if (type.value === 'beforeActive') return t('midAutumn.distanceToStart')
  if (type.value === 'activing') return t('midAutumn.distanceToEnd')
  if (type.value === 'rewarding') return t('midAutumn.distanceToEnd')
  return t('midAutumn.distanceToEnd')
})

const countDown = computed(() => {
  const now = Number(props.realTime) || 0
  let target = startTime.value
  if (type.value === 'activing') target = endTime.value
  if (type.value === 'rewarding') target = closeTime.value
  if (type.value === 'end') target = now

  const diff = Math.max(0, target - now)
  const totalSeconds = Math.floor(diff / 1000)
  const day = Math.floor(totalSeconds / 86400)
  const hour = Math.floor((totalSeconds % 86400) / 3600)
  const minute = Math.floor((totalSeconds % 3600) / 60)
  const second = totalSeconds % 60
  return {
    day: String(day).padStart(2, '0'),
    hour: String(hour).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    second: String(second).padStart(2, '0'),
  }
})
</script>

<style lang="less" scoped src="./pc_style.less"></style>
<style lang="less" scoped src="./mobile_style.less"></style>
